import React from "react";
import { I18n } from "@aws-amplify/core";
import update from "immutability-helper";
import { connect } from "react-redux";
import { withStyles } from "@material-ui/styles";
import { get, map, size, filter, keys, concat, groupBy } from "lodash";

import { fetchDelete, fetchGet, fetchPut } from "../../../api/fetch";

import Grid from "@material-ui/core/Grid";
import Box from "@material-ui/core/Box";

import AddAssociation from "./AddAssociation";
import CustomButton from "../../Common/CustomButton";
import ProfileAssociationsRow from "./ProfileAssociationsRow";
import ScrollableTable from "../../Common/Table/ScrollableTable";

import { getLov } from "../../../utils/lov";

import colors from "../../../assets/style/colors";
import images from "../../../assets/img/images";
import { isAllowed } from "../../../utils/users";

const columnsIndividuals = [
  {
    name: "profiles_name",
    minWidth: 250,
  },
  {
    name: "profiles_association",
    minWidth: 150,
  },
  {
    name: "profiles_tag",
    minWidth: 150,
  },
  {
    name: "profiles_phone",
    id: 34,
    minWidth: 250,
  },
  {
    name: "profiles_email",
    id: 33,
    minWidth: 220,
  },
];

const columnsObjects = [
  {
    name: "profiles_name",
    minWidth: 250,
  },
  {
    name: "profiles_association",
    minWidth: 150,
  },
  {
    name: "profiles_tag",
    minWidth: 150,
  },
  {
    name: "profiles_carrier",
    id: 89,
    minWidth: 150,
  },
  {
    name: "profiles_lpr",
    id: 44,
    minWidth: 150,
  },
  {
    name: "profiles_ocr",
    id: 50,
    minWidth: 150,
  },
  {
    name: "profiles_type",
    minWidth: 150,
  },
];

const styles = (theme) => ({
  card: {
    backgroundColor: colors.white,
    height: "calc(100% - 24px)",
    overflow: "auto",
    borderRadius: "2px",
    [theme.breakpoints.up("sm")]: {
      maxHeight: "calc(100vh - 200px)",
      height: "calc(100vh - 200px)",
      margin: "12px",
      boxShadow: "0px 2px 2px 0px " + colors.black23,
    },
    [theme.breakpoints.down("sm")]: {
      margin: "12px 6px",
      maxHeight: "calc(100vh - 160px)",
      height: "calc(100vh - 160px)",
    },
  },
  contentContainer: {
    [theme.breakpoints.up("sm")]: {
      maxWidth: "1270px",
      padding: "24px",
    },
    [theme.breakpoints.down("sm")]: {
      padding: "12px",
    },
  },
  dataCell: {
    color: colors.black54,
  },
  tableContainer: {
    width: "100%",
    maxWidth: "1222px",
    overflow: "auto",
    marginBottom: "50px",
  },
});

const ProfileAssociations = (props) => {
  const { classes, language, profile, permissions } = props;
  const [associations, setAssociations] = React.useState();
  const [lov, setLov] = React.useState();
  const [isFetching, setIsFetching] = React.useState(false);
  const [editMode, setEditMode] = React.useState(false);
  const [edits, setEdits] = React.useState({});

  React.useEffect(() => {
    fetchAssociations();
    fetchLov();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [get(profile, ["id"])]);

  const fetchAssociations = async () => {
    setIsFetching(true);
    try {
      const associations = await fetchGet(
        `profile_associations/profiles/${get(profile, ["id"])}?active=true`
      );
      
      setAssociations(associations);
    } catch (e) {
      console.log(e);
    }
    setIsFetching(false);
  };
  const fetchLov = async () => {
    try {
      setLov({
        89: await getLov(89),
        association_types: await fetchGet("association_types?active=true"),
      });
    } catch (e) {
      console.log(e);
    }
  };

  const handleEdit = (assId, value) => {
    setEdits(
      update(edits, {
        [assId]: { $set: value },
      })
    );
  };

  const applyEdits = async () => {
    try {
      const ids = keys(edits);
      for (let i = 0; i < size(ids); i++) {
        if (edits[ids[i]] === null) {
          await fetchDelete(`profile_associations/${ids[i]}`, null);
        } else {
          await fetchPut(`profile_associations/${ids[i]}`, {
            p1_association_type_id: edits[ids[i]],
          });
        }
      }
      setEditMode(false);
      fetchAssociations();
    } catch (e) {
      console.log(e);
    }
  };

  return (
    <Box className={classes.card}>
      <Grid container direction="column" className={classes.contentContainer}>
        <Grid
          item
          container
          alignItems="center"
          style={{ marginBottom: "24px" }}
        >
          <AddAssociation
            profile={profile}
            refresh={fetchAssociations}
            permissions={permissions}
          />
          <Grid
            item
            className="text-54 text-italic"
            style={{ marginLeft: "30px" }}
          >
            {I18n.get("profiles_associateThisProfileWithOtherProfiles")}
          </Grid>
          <Grid item xs />
          <Grid item>
            {editMode ? (
              <>
                <CustomButton
                  color="primary"
                  onClick={() => {
                    setEditMode(false);
                    setEdits({});
                  }}
                >
                  {I18n.get("site_cancel")}
                </CustomButton>
                <CustomButton
                  variant="contained"
                  color="primary"
                  style={{ marginLeft: "10px" }}
                  onClick={applyEdits}
                >
                  {I18n.get("site_confirm")}
                </CustomButton>
              </>
            ) : size(associations) > 0 ? (
              <img
                src={
                  !isAllowed(permissions, "profile_associations.update")
                    ? images.editIconDisabled
                    : images.editIconColored
                }
                alt="edit"
                style={{
                  cursor: !isAllowed(permissions, "profile_associations.update")
                    ? ""
                    : "pointer",
                }}
                onClick={() =>
                  !isAllowed(permissions, "profile_associations.update")
                    ? ""
                    : setEditMode(true)
                }
              />
            ) : (
              <></>
            )}
          </Grid>
        </Grid>
        {size(
          filter(
            associations,
            (ass) => get(ass, ["p1_profile", "type", "id"]) === 1
          )
        ) > 0 && (
          <>
            <Grid
              item
              className="bold-500"
              style={{ textTransform: "uppercase" }}
            >
              {I18n.get("profiles_individuals")}
            </Grid>
            <Grid item className={classes.tableContainer}>
              <ScrollableTable
                columns={
                  editMode
                    ? concat(columnsIndividuals, [{ minWidth: 35, width: 35 }])
                    : columnsIndividuals
                }
                hasMore={false}
                isFetching={isFetching}
                hideLoadingBar
              >
                {map(
                  // Group by p1 profile if not in edit mode
                  editMode
                    ? filter(
                        associations,
                        (ass) =>
                          get(ass, ["p1_profile", "type", "id"]) === 1 &&
                          get(edits, [ass.id]) !== null
                      )
                    : map(
                        groupBy(
                          filter(
                            associations,
                            (ass) =>
                              get(ass, ["p1_profile", "type", "id"]) === 1
                          ),
                          (ass) => get(ass, ["p1_profile", "id"])
                        ),
                        (v, k) => v
                      ),
                  (ass, index) => (
                    <ProfileAssociationsRow
                      key={index}
                      association={ass}
                      index={index}
                      language={language}
                      editMode={editMode}
                      lov={lov}
                      handleEdit={handleEdit}
                      edits={edits}
                    />
                  )
                )}
              </ScrollableTable>
            </Grid>
          </>
        )}
        {size(
          filter(
            associations,
            (ass) => get(ass, ["p1_profile", "type", "id"]) !== 1
          )
        ) > 0 && (
          <>
            <Grid
              item
              className="bold-500"
              style={{ textTransform: "uppercase" }}
            >
              {I18n.get("profiles_objects")}
            </Grid>
            <Grid item className={classes.tableContainer}>
              <ScrollableTable
                columns={
                  editMode
                    ? concat(columnsObjects, [{ minWidth: 35, width: 35 }])
                    : columnsObjects
                }
                hasMore={false}
                isFetching={isFetching}
                //stickyHeader
                hideLoadingBar
              >
                {map(
                  // Group by p1 profile if not in edit mode
                  editMode
                    ? filter(
                        associations,
                        (ass) =>
                          get(ass, ["p1_profile", "type", "id"]) !== 1 &&
                          get(edits, [ass.id]) !== null
                      )
                    : map(
                        groupBy(
                          filter(
                            associations,
                            (ass) =>
                              get(ass, ["p1_profile", "type", "id"]) !== 1
                          ),
                          (ass) => get(ass, ["p1_profile", "id"])
                        ),
                        (v, k) => v
                      ),
                  (ass, index) => (
                    <ProfileAssociationsRow
                      key={index}
                      association={ass}
                      index={index}
                      language={language}
                      editMode={editMode}
                      lov={lov}
                      handleEdit={handleEdit}
                      edits={edits}
                    />
                  )
                )}
              </ScrollableTable>
            </Grid>
          </>
        )}
      </Grid>
    </Box>
  );
};

const mapStateToProps = (state) => {
  return {
    language: get(state, ["user", "language"]),
  };
};

export default connect(mapStateToProps)(
  withStyles(styles)(ProfileAssociations)
);
